import {useParams} from "react-router-dom";
import {useState, useEffect, forwardRef, useImperativeHandle} from "react";
import {Stack, Typography, Box, Button, Paper, Grid2} from "@mui/material";
import {
  IGetExamConfig,
  useGetExamConfigQuery,
} from "../../../../store/api/examConfigApiSlice";
import SubjectButtonUI from "./SubjectButtonUI";
import ChapterListItemUI from "./ChapterListItemUI";
import {SearchButtonDropdownUseRefType} from "../../../../components/search/search-button-dropdown/search-button-dropdown-main";
import {ExamPageSection} from "../../../../pages/exam/common/navigation/routes";
import {useExamPageNavigate} from "../../../../pages/exam/common/navigation/useExamPageNavigate";

type SyllabusDropdownProps = {
  handleDropdownClose: () => void;
};

const SyllabusDropdown = forwardRef<
  SearchButtonDropdownUseRefType,
  SyllabusDropdownProps
>(({handleDropdownClose}, ref) => {
  const {subjectId: subjectParamId, chapterId: chapterParamId} = useParams();
  const {currentData} = useGetExamConfigQuery();

  // subjects
  const subjects = currentData?.subjects ?? [];
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(
    subjects.find(subject => subject.id === subjectParamId)?.id ??
      subjects[0]?.id ??
      null,
  );

  // chapters
  const chapters = currentData?.chapters[selectedSubjectId ?? ""] ?? [];
  const defaultChapter =
    chapters.find(chapter => chapter.id === chapterParamId) ??
    chapters[0] ??
    null;
  type ChapterType = IGetExamConfig["chapters"][string][number];
  const [selectedChapter, setSelectedChapter] = useState<ChapterType | null>(
    defaultChapter,
  );

  useEffect(() => {
    if (selectedSubjectId) {
      setSelectedChapter(defaultChapter);
    }
  }, [selectedSubjectId, chapterParamId, defaultChapter]);

  /** ACTIONS */
  const examPageNavigate = useExamPageNavigate();
  useImperativeHandle(ref, () => ({
    closeDropdown() {
      if (selectedSubjectId && selectedChapter?.id) {
        examPageNavigate(ExamPageSection.PROBLEM_SET_CHAPTER, {
          subjectId: selectedSubjectId,
          chapterId: selectedChapter?.id,
        });
      }
    },
    applyFilters() {},
  }));

  const onChapterSelectClick = (chapter: ChapterType) => {
    setSelectedChapter(chapter);
  };

  return (
    <>
      <Box
        sx={{
          zIndex: 9,
          height: "fit-content",
          width: "100%",
          position: "absolute",
          top: 0,
          p: 2.5,
          pb: 1.5,
          backgroundColor: "white",
          border: "1px solid",
          borderColor: "secondary.main",
          borderRadius: 0,
        }}>
        <Typography fontWeight={"600"} fontSize={"1rem"}>
          Which subject and chapter?
        </Typography>
      </Box>
      <Stack spacing={"12px"} p={2.5} width={"700px"} mt={7}>
        <Stack spacing={2} direction={"row"} mb={0.5}>
          {subjects.map(subject => (
            <SubjectButtonUI
              key={subject.id}
              subjectItem={subject}
              isSelected={selectedSubjectId === subject.id}
              onClick={() => {
                setSelectedSubjectId(subject.id);
              }}
            />
          ))}
        </Stack>
        <Box
          m={0}
          height={"0.5px"}
          borderBottom={"1px solid"}
          borderColor={"secondary.main"}
          width={"100%"}
        />
        <Grid2
          container
          spacing={1}
          maxHeight={"60vh"}
          overflow={"scroll"}
          mt={"0!important"}
          pt={1.5}
          pb={7}>
          {chapters.map(chapter => (
            <Grid2 size={6} key={chapter.id}>
              <ChapterListItemUI
                key={chapter.id}
                chapterItem={chapter}
                isSelected={selectedChapter?.id === chapter.id}
                onClick={onChapterSelectClick}
              />
            </Grid2>
          ))}
        </Grid2>
      </Stack>
      <Box
        sx={{
          zIndex: 9,
          height: "fit-content",
          width: "100%",
          position: "absolute",
          bottom: 0,
          backgroundColor: "white",
          border: "1px solid",
          borderColor: "secondary.main",
          borderRadius: 0,
        }}>
        <Stack
          p={1.5}
          px={2.5}
          direction={"row"}
          justifyContent={"end"}
          alignItems={"center"}
          spacing={1}>
          <Button variant="contained" onClick={handleDropdownClose}>
            Select&nbsp;<b>{selectedChapter?.viewModel.name}</b>
          </Button>
        </Stack>
      </Box>
    </>
  );
});

SyllabusDropdown.displayName = "SyllabusDropdown";
export default SyllabusDropdown;
